import React from 'react';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { SmallPortalTooltip } from '../Tooltip';
import { LabelUppercase, P, PSmall } from '../elements/Text';
import { Link, useParams } from 'react-router-dom';
import groupby from 'lodash.groupby';

import {
  useGetCompany,
  useGetCompanyOfficeHours,
} from '../../hooks/use-hydra/admin';
import { CompanyResponse, OfficeHoursResponse } from '../../api/admin';
import { LEARNING_PLAN_UNIT_TYPE } from '../LearningPlan/LearningPlanUnit';
import pluralize from 'pluralize';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import {
  TooltipContent,
  TooltipDetail,
  TooltipDetailHighlight,
  TooltipTitle,
} from './Progress/Tooltip';
import {
  FiltersHorizontalWrapper,
  MultiSelectFilterPill,
  TextFilterPill,
} from '../elements/Filter';
import { ROUTES } from '../../routing';

dayjs.extend(isSameOrAfter);

const SubcohortCard = styled.div`
  background-color: ${({ theme }) => theme.colors.bgPage};
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  transition: all 0.3s ease-in-out;
`;

const SubcohortHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.bgActive};
`;

const MentorSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const MentorIcon = styled.i.attrs({
  className: 'fas fa-chalkboard-teacher',
})`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.bgDefaultSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 14px;
  font-weight: 600;
`;

const MentorInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MentorName = styled(PSmall)`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
  padding: 0;
`;

const MentorLabel = styled(LabelUppercase)`
  color: ${({ theme }) => theme.colors.textRegular} !important;
  margin: 0;
  padding: 0;
`;

const NextCallBadge = styled.div`
  background-color: ${({ theme }) => theme.colors.primary}15;
  border: 1px solid ${({ theme }) => theme.colors.primary}30;
  border-radius: 6px;
  padding: 6px 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 12px;
  font-weight: 500;
`;

const ClockIcon = styled.i`
  font-size: 12px;
`;

const ContentSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const LearnersSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const SectionIcon = styled.i`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textRegular};
`;

const SectionTitle = styled(LabelUppercase)`
  color: ${({ theme }) => theme.colors.textRegular};
  margin: 0;
  padding: 0;
`;

const LearnersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const LearnerItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  /* background-color: ${({ theme }) => theme.colors.bgDefaultSecondary}; */
  /* border: 1px solid ${({ theme }) => theme.colors.bgActive}; */
  border-radius: 8px;
  transition: all 0.3s ease-in-out;

  &:hover {
    border-color: ${({ theme }) => theme.colors.bgActive};
    background-color: ${({ theme }) => theme.colors.bgActive}50;
  }
`;

const LearnerInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const LearnerName = styled(PSmall)`
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
  font-weight: 500;
  padding: 0;
`;

const ProgressSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const ProgressBarContainer = styled.div`
  height: 6px;
  width: 80px;
  background-color: ${({ theme }) => theme.colors.bgActive};
  border-radius: 3px;
  overflow: hidden;
`;

const ProgressBarFill = styled.div<{ progress: number }>`
  height: 100%;
  width: ${({ progress }) => progress}%;
  background-color: ${({ theme }) => theme.colors.primary};
  border-radius: 3px;
  transition: width 0.3s ease-in-out;
`;

const ProgressText = styled(PSmall)`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
  padding: 0;
  font-size: 11px;
  font-weight: 500;
`;

const ProgressLabel = styled(PSmall)`
  color: ${({ theme }) => theme.colors.textRegular};
  margin: 0;
  padding: 0;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const OfficeHoursSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const OfficeHoursList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const OfficeHourItem = styled(Link)<{
  isNext?: boolean;
  isPast?: boolean;
  isCurrent?: boolean;
}>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border-radius: 6px;
  background-color: ${({ isNext, isPast, isCurrent, theme }) => {
    if (isCurrent) return theme.colors.primary + '10';
    if (isNext) return theme.colors.primary + '10';
    if (isPast) return theme.colors.bgDefaultSecondary;
    return theme.colors.bgDefaultSecondary;
  }};
  border: ${({ isNext, isCurrent, theme }) =>
    isNext || isCurrent
      ? `1px solid ${theme.colors.primary}30`
      : `1px solid ${theme.colors.bgActive}`};
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  text-decoration: none;
  color: inherit;

  &:hover {
    background-color: ${({ isNext, isCurrent, theme }) =>
      isNext || isCurrent
        ? theme.colors.primary + '20'
        : theme.colors.bgActive};
    border-color: ${({ isNext, isCurrent, theme }) =>
      isNext || isCurrent
        ? theme.colors.primary + '50'
        : theme.colors.bgActive};
    text-decoration: none;
    color: inherit;
  }

  ${({ isPast }) =>
    isPast &&
    `
    opacity: 0.7;
  `}
`;

const OfficeHourTime = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const WeekLabel = styled(PSmall)<{
  isNext?: boolean;
  isPast?: boolean;
  isCurrent?: boolean;
}>`
  margin: 0;
  font-weight: 500;
  color: ${({ isNext, isPast, isCurrent, theme }) => {
    if (isCurrent) return theme.colors.primary;
    if (isNext) return theme.colors.primary;
    if (isPast) return theme.colors.textRegular;
    return theme.colors.textRegular;
  }};
  padding: 0;
  font-size: 13px;
`;

const TimeLabel = styled(PSmall)<{
  isNext?: boolean;
  isPast?: boolean;
  isCurrent?: boolean;
}>`
  margin: 0;
  color: ${({ isNext, isPast, isCurrent, theme }) => {
    if (isCurrent) return theme.colors.primary;
    if (isNext) return theme.colors.primary;
    if (isPast) return theme.colors.textPrimary;
    return theme.colors.textPrimary;
  }};
  font-size: 12px;
  padding: 0;
`;

const StatusBadge = styled.div<{
  isNext?: boolean;
  isPast?: boolean;
  isCurrent?: boolean;
}>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  ${({ isNext, isCurrent, theme }) => {
    if (isCurrent || isNext) {
      return `
        background-color: ${theme.colors.primary}20;
        color: ${theme.colors.primary};
        border: 1px solid ${theme.colors.primary}40;
      `;
    }
    return `
      background-color: ${theme.colors.bgActive};
      color: ${theme.colors.textPrimary};
      border: 1px solid ${theme.colors.bgActive};
    `;
  }}
`;

const CohortSection = styled.div`
  margin-bottom: 40px;
`;

const CohortHeader = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const CohortTitle = styled.h3`
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
  padding: 0;
  font-size: 18px;
  font-weight: 600;
`;

const CohortStats = styled(LabelUppercase)`
  color: ${({ theme }) => theme.colors.textRegular};
  margin: 0;
  padding: 0;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// Function to calculate learning plan progress (copied from Progress.tsx)
const calculateLearningPlanProgress = (
  learnerId: number,
  companyData: CompanyResponse
): number | null => {
  // Find the learner in the company data
  const learner = companyData.learners?.find((l) => l.id === learnerId);
  if (!learner?.learningPlan) {
    return null;
  }

  let totalItems = 0;
  let completedItems = 0;

  for (const planItem of learner.learningPlan) {
    if (planItem.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE) {
      // For modules, count core activities
      const coreActivitiesCount = planItem.coreLessonsCount || 0;
      const coreSubmittedCount = planItem.coreSubmittedLessonsCount || 0;

      totalItems += coreActivitiesCount;
      completedItems += Math.min(coreSubmittedCount, coreActivitiesCount);
    } else if (planItem.type === LEARNING_PLAN_UNIT_TYPE.PROJECT) {
      // For projects, count as item
      totalItems += 1;
      // Project is completed if submittedExercisesCount > 0
      if ((planItem.submittedExercisesCount || 0) > 0) {
        completedItems += 1;
      }
    }
  }

  if (totalItems === 0) {
    return null;
  }

  return Math.round((completedItems / totalItems) * 100);
};

export default function SubcohortsAndCalls() {
  const { id } = useParams<{ id: string }>();

  const { data: companyData } = useGetCompany(parseInt(id));
  const { data: officeHours } = useGetCompanyOfficeHours(parseInt(id));

  const userTimezone = dayjs.tz.guess();
  const timezoneOffset = dayjs().tz(userTimezone).format('Z');

  const getOfficeHoursWithWeeks = React.useCallback(
    (
      officeHours: OfficeHoursResponse['officeHoursGroups'][number]['officeHours'],
      cohortStartDate: Date
    ) => {
      const startDate = dayjs.utc(cohortStartDate);
      const now = dayjs();

      return officeHours
        .map((officeHour) => {
          const officeHourDate = dayjs.utc(officeHour.timestamp);
          const weekNumber =
            Math.floor(officeHourDate.diff(startDate, 'day') / 7) + 1;
          const localTime = officeHourDate.tz(userTimezone);

          // Check if the office hour is currently happening (within 1 hour of start)
          const isCurrent =
            now.isAfter(officeHourDate) &&
            now.isBefore(officeHourDate.add(1, 'hour'));
          const isNext = !isCurrent && officeHourDate.isAfter(now);
          const isPast = !isCurrent && officeHourDate.isBefore(now);

          return {
            timestamp: officeHour.timestamp,
            officeHourId: officeHour.officeHourId,
            weekNumber,
            localTime,
            isNext,
            isPast,
            isCurrent,
            fromNow: localTime.fromNow(),
          };
        })
        .sort((a, b) => {
          if ((a.isCurrent || a.isNext) && b.isPast) return -1;
          if ((b.isCurrent || b.isNext) && a.isPast) return 1;
          return dayjs(a.timestamp).diff(dayjs(b.timestamp));
        });
    },
    [userTimezone]
  );

  const getNextOfficeHour = React.useCallback(
    (officeHoursData: ReturnType<typeof getOfficeHoursWithWeeks>) => {
      const currentCall = officeHoursData.find((oh) => oh.isCurrent);
      if (currentCall) return currentCall;
      return officeHoursData.find((oh) => oh.isNext);
    },
    []
  );

  const [mentorName, setMentorName] = React.useState('');

  const [learnerName, setLearnerName] = React.useState('');

  const DAYS_OF_WEEK = [
    { label: 'Monday', value: 'Monday' },
    { label: 'Tuesday', value: 'Tuesday' },
    { label: 'Wednesday', value: 'Wednesday' },
    { label: 'Thursday', value: 'Thursday' },
    { label: 'Friday', value: 'Friday' },
    { label: 'Saturday', value: 'Saturday' },
    { label: 'Sunday', value: 'Sunday' },
  ];
  const [selectedDays, setSelectedDays] = React.useState<string[]>([]);

  const orderedAndFilteredOfficeHours = React.useMemo(() => {
    if (!officeHours) return [];
    return officeHours.officeHoursGroups
      .filter(
        (oh) =>
          oh.mentor.name.toLowerCase().includes(mentorName.toLowerCase()) &&
          (!learnerName.trim() ||
            oh.members.some((member) =>
              member.name.toLowerCase().includes(learnerName.toLowerCase())
            )) &&
          (selectedDays.length === 0 ||
            oh.officeHours.some((officeHour) => {
              const dayName = dayjs(officeHour.timestamp).format('dddd');
              return selectedDays.includes(dayName);
            }))
      )
      .sort((a, b) => {
        const aGroupCalls = getOfficeHoursWithWeeks(
          a.officeHours,
          a.cohort.startDate
        );
        const bGroupCalls = getOfficeHoursWithWeeks(
          b.officeHours,
          b.cohort.startDate
        );
        const aNextCall = aGroupCalls.find((oh) => oh.isCurrent || oh.isNext);
        const bNextCall = bGroupCalls.find((oh) => oh.isCurrent || oh.isNext);

        // If both have upcoming calls, compare their timestamps
        if (aNextCall && bNextCall) {
          return dayjs(aNextCall.timestamp).diff(dayjs(bNextCall.timestamp));
        }

        // If only one has an upcoming call, it should come first
        if (aNextCall) return -1;
        if (bNextCall) return 1;

        // If neither has upcoming calls, sort by their most recent past call
        const aLastCall = aGroupCalls[aGroupCalls.length - 1];
        const bLastCall = bGroupCalls[bGroupCalls.length - 1];

        return dayjs(bLastCall.timestamp).diff(dayjs(aLastCall.timestamp));
      });
  }, [
    officeHours,
    mentorName,
    learnerName,
    selectedDays,
    getOfficeHoursWithWeeks,
  ]);

  const officeHoursByCohorts = React.useMemo(
    () =>
      Object.entries(groupby(orderedAndFilteredOfficeHours, 'cohort.id')).sort(
        (a, b) => {
          return dayjs(b[1][0].cohort.startDate).diff(
            dayjs(a[1][0].cohort.startDate)
          );
        }
      ),
    [orderedAndFilteredOfficeHours]
  );

  const getLearnerProgress = (learnerEmail: string) => {
    if (!companyData) return null;

    for (const cohort of companyData.cohorts) {
      const learner = cohort.learners.find((l) => l.email === learnerEmail);
      if (learner) {
        return calculateLearningPlanProgress(learner.learnerId, companyData);
      }
    }
    return null;
  };

  const learnerInfoTooltips: Array<{
    id: string;
    email: string;
    role: string | null;
  }> = [];
  const learnerProgressTooltips: Array<{
    id: string;
    progress: number;
    skillRatings: Array<{
      skill: string;
      rating: number;
    }>;
  }> = [];

  return (
    <div style={{ marginTop: '32px' }}>
      <FiltersHorizontalWrapper>
        <TextFilterPill
          label="Mentor"
          value={mentorName}
          onChange={setMentorName}
        />
        <TextFilterPill
          label="Learner"
          value={learnerName}
          onChange={setLearnerName}
        />

        <MultiSelectFilterPill
          label="Day of week"
          values={selectedDays}
          options={DAYS_OF_WEEK.map((day) => ({
            label: day.label,
            value: day.value,
          }))}
          onChange={setSelectedDays}
        />
      </FiltersHorizontalWrapper>
      {officeHoursByCohorts.map(([cohortId, cohortGroup]) => {
        const cohortTitle =
          dayjs.utc(cohortGroup[0].cohort.startDate).format('MMM D, YYYY') +
          ' - ' +
          dayjs.utc(cohortGroup[0].cohort.endDate).format('MMM D, YYYY');

        return (
          <CohortSection key={cohortId}>
            <CohortHeader>
              <CohortTitle>{cohortTitle} Cohort</CohortTitle>
              <CohortStats>
                {cohortGroup.length}{' '}
                {pluralize('sub-cohort', cohortGroup.length)}
              </CohortStats>
            </CohortHeader>
            {cohortGroup.map((group) => {
              const parsedOfficeHours = getOfficeHoursWithWeeks(
                group.officeHours,
                cohortGroup[0].cohort.startDate
              );
              const nextOfficeHour = getNextOfficeHour(parsedOfficeHours);

              return (
                <SubcohortCard key={group.id}>
                  <SubcohortHeader>
                    <MentorSection>
                      <MentorIcon />
                      <MentorInfo>
                        <MentorLabel>Mentor</MentorLabel>
                        <MentorName>
                          <span
                            data-tooltip-id={group.mentor.mentorProfileId.toString()}
                          >
                            {group.mentor.name}
                          </span>
                        </MentorName>
                      </MentorInfo>
                    </MentorSection>
                    {(nextOfficeHour?.isCurrent || nextOfficeHour?.isNext) && (
                      <NextCallBadge>
                        <ClockIcon className="fas fa-clock" />
                        {nextOfficeHour.isCurrent
                          ? 'Call happening now'
                          : nextOfficeHour.localTime.isSame(
                              dayjs().add(1, 'day'),
                              'day'
                            )
                          ? 'Next call tomorrow'
                          : `Next call ${nextOfficeHour.fromNow}`}
                      </NextCallBadge>
                    )}
                  </SubcohortHeader>

                  <ContentSection>
                    <LearnersSection>
                      <SectionHeader>
                        <SectionIcon className="fas fa-users" />
                        <SectionTitle>
                          {pluralize('Learner', group.members.length)} (
                          {group.members.length})
                        </SectionTitle>
                      </SectionHeader>
                      <LearnersList>
                        {group.members.map((member) => {
                          const progress = getLearnerProgress(member.email);
                          const learnerSkillsRating = companyData?.cohorts
                            .find((c) => c.cycleId === parseInt(cohortId))
                            ?.learners.find(
                              (l) => l.learnerId === member.learnerId
                            )?.onboardingAnswers.skillRatings;
                          const learnerRole = companyData?.learners.find(
                            (l) => l.id === member.learnerId
                          )?.onboardingAnswers.role;
                          learnerInfoTooltips.push({
                            id: `member-${member.id}`,
                            email: member.email,
                            role: learnerRole ?? null,
                          });

                          learnerProgressTooltips.push({
                            id: `progress-label-${member.id}`,
                            progress: progress ?? 0,
                            skillRatings: learnerSkillsRating
                              ? Object.entries(learnerSkillsRating).map(
                                  ([skill, rating]) => ({
                                    skill,
                                    rating,
                                  })
                                )
                              : [],
                          });

                          return (
                            <LearnerItem key={`${member.email}-${member.id}`}>
                              <LearnerInfo>
                                <ProgressLabel> Learner</ProgressLabel>
                                <LearnerName>
                                  <span data-tooltip-id={`member-${member.id}`}>
                                    {member.name}
                                  </span>
                                </LearnerName>
                              </LearnerInfo>
                              {progress !== null && (
                                <ProgressSection
                                  data-tooltip-id={`progress-label-${member.id}`}
                                >
                                  <ProgressLabel>
                                    Learning Plan Progress
                                  </ProgressLabel>
                                  <Row>
                                    <ProgressBarContainer>
                                      <ProgressBarFill progress={progress} />
                                    </ProgressBarContainer>
                                    <ProgressText>{progress}%</ProgressText>
                                  </Row>
                                </ProgressSection>
                              )}
                            </LearnerItem>
                          );
                        })}
                      </LearnersList>
                    </LearnersSection>

                    <OfficeHoursSection>
                      <SectionHeader>
                        <SectionIcon className="fas fa-calendar-alt" />
                        <SectionTitle>
                          Office Hours ({userTimezone} {timezoneOffset})
                        </SectionTitle>
                      </SectionHeader>
                      <OfficeHoursList>
                        {parsedOfficeHours.map((officeHour) => {
                          const isNextCall =
                            officeHour.isNext && officeHour === nextOfficeHour;

                          return (
                            <OfficeHourItem
                              key={officeHour.officeHourId.toString()}
                              isNext={isNextCall}
                              isPast={officeHour.isPast}
                              isCurrent={officeHour.isCurrent}
                              to={ROUTES.ADMIN.OFFICE_HOURS_SUBCOHORT.getPath({
                                companyId: id,
                                officeHourId:
                                  officeHour.officeHourId.toString(),
                              })}
                            >
                              <OfficeHourTime>
                                <WeekLabel
                                  isNext={isNextCall}
                                  isPast={officeHour.isPast}
                                  isCurrent={officeHour.isCurrent}
                                >
                                  Week {officeHour.weekNumber}
                                </WeekLabel>
                                <TimeLabel
                                  isNext={isNextCall}
                                  isPast={officeHour.isPast}
                                  isCurrent={officeHour.isCurrent}
                                >
                                  {officeHour.localTime.format('MMM D, h:mm A')}
                                </TimeLabel>
                              </OfficeHourTime>
                              <StatusBadge
                                isNext={isNextCall}
                                isPast={officeHour.isPast}
                                isCurrent={officeHour.isCurrent}
                              >
                                {officeHour.isCurrent
                                  ? 'Current'
                                  : isNextCall
                                  ? 'Next'
                                  : officeHour.isPast
                                  ? 'Past'
                                  : 'Upcoming'}
                              </StatusBadge>
                            </OfficeHourItem>
                          );
                        })}
                      </OfficeHoursList>
                    </OfficeHoursSection>
                  </ContentSection>
                </SubcohortCard>
              );
            })}
          </CohortSection>
        );
      })}
      {Object.entries(officeHoursByCohorts).length === 0 && (
        <P style={{ fontStyle: 'italic' }}>Can't find any office hours.</P>
      )}

      {learnerInfoTooltips.map((tooltip) => (
        <SmallPortalTooltip
          key={tooltip.id}
          anchorSelect={`[data-tooltip-id="${tooltip.id}"]`}
        >
          <TooltipContent>
            <TooltipDetail>{tooltip.email}</TooltipDetail>
            {tooltip.role && (
              <TooltipDetail>Role: {tooltip.role}</TooltipDetail>
            )}
          </TooltipContent>
        </SmallPortalTooltip>
      ))}
      {learnerProgressTooltips.map((tooltip) => (
        <SmallPortalTooltip
          key={tooltip.id}
          anchorSelect={`[data-tooltip-id="${tooltip.id}"]`}
        >
          <TooltipContent>
            <TooltipTitle>Latest Plan Progress</TooltipTitle>
            <TooltipDetail>{tooltip.progress}%</TooltipDetail>
            {(tooltip.skillRatings ?? []).length ? (
              <>
                <TooltipTitle style={{ marginTop: 16 }}>
                  Skill Ratings
                </TooltipTitle>
                {tooltip.skillRatings.map(({ skill, rating }, index) => (
                  <TooltipDetail key={skill}>
                    {index + 1}.{' '}
                    <TooltipDetailHighlight>{skill}</TooltipDetailHighlight> -{' '}
                    {rating}/10
                  </TooltipDetail>
                ))}
              </>
            ) : null}
          </TooltipContent>
        </SmallPortalTooltip>
      ))}
    </div>
  );
}
